import { IonButton, IonCard, IonCardContent, IonIcon, useIonModal } from '@ionic/react';
import { StarRating } from 'react-flexible-star-rating';
import { useMemo, useState } from 'react';
import { closeOutline } from 'ionicons/icons';
import { useTranslation } from '../hooks/useTranslation';
import RateExperienceFollowupModal from '../modals/RateExperienceFollowupModal';
import { useSetUserExperienceRatingPromptResultMutation, useUserExperienceRatingPromptQuery } from '../query/userExperienceRatingPromptQuery';
import styles from './RateExperienceBanner.module.css';

const MAX_STARS = 5;

const RateExperienceBanner: React.FC = () => {
    const { t } = useTranslation('components');
    const [rating, setRating] = useState<number>(0);
    const [isDismissed, setIsDismissed] = useState(false);
    const promptQuery = useUserExperienceRatingPromptQuery();
    const setPromptResultMutation = useSetUserExperienceRatingPromptResultMutation();
    const prompt = promptQuery.data?.prompt;
    const promptId = prompt?.id;
    const [presentFollowupModal, dismissFollowupModal] = useIonModal(RateExperienceFollowupModal, {
        dismiss: (data?: string | null | undefined | number, role?: string) => dismissFollowupModal(data, role),
        rating,
        promptId,
        onComplete: () => setIsDismissed(true)
    });

    const handleRatingChange = (nextRating: number) => {
        setRating(nextRating);

        if (nextRating <= 0) {
            return;
        }

        if (promptId) {
            setPromptResultMutation.mutate({ promptId, rating: nextRating });
        }

        presentFollowupModal({
            presentingElement: document.getElementsByTagName('ion-router-outlet')[0],
            cssClass: 'oc-dialog-s'
        });
    };

    const helperText = useMemo(() => {
        if (rating <= 0) {
            return t('rate_experience_banner.tap_to_rate');
        }

        return t('rate_experience_banner.thanks', { rating, max: MAX_STARS });
    }, [rating, t]);

    if (isDismissed || promptQuery.isLoading || promptQuery.isError || !promptQuery.data?.shouldPrompt || !prompt) {
        return null;
    }

    return (
        <IonCard className={styles.card}>
            <IonCardContent className={styles.content}>
                <IonButton
                    className={styles.closeButton}
                    fill="clear"
                    size="small"
                    onClick={() => {
                        if (promptId) {
                            setPromptResultMutation.mutate({ promptId, actionTaken: "dismissed" });
                        }
                        setIsDismissed(true);
                    }}
                    aria-label={t('rate_experience_banner.dismiss')}
                >
                    <IonIcon icon={closeOutline} />
                </IonButton>
                <div className={styles.title}>{t('rate_experience_banner.title')}</div>
                <div className={styles.subtitle}>{t('rate_experience_banner.subtitle')}</div>
                <div className={styles.stars} aria-label={t('rate_experience_banner.aria_label')}>
                    <StarRating
                        starsLength={MAX_STARS}
                        initialRating={0}
                        dimension={8}
                        onRatingChange={handleRatingChange}
                    />
                </div>
                <div className={styles.helper}>{helperText}</div>
            </IonCardContent>
        </IonCard>
    );
};

export default RateExperienceBanner;
